import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useRegisterInput from '../Form/useRegisterInput';
import useArrayField from '../ArrayField/useArrayField';
import { useFormContext } from '../Form';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Adjustment from './Adjustment';
import capitalize from '@afosto/utils/string/capitalize';
import uuid from '@afosto/utils/uuid';
import translations from './translations';

export const ADJUSTMENT_STRUCTURE = {
  reference: uuid(),
  description: '',
  amount: '',
  isPercentage: false,
  isDiscount: true,
};

const AdjustmentsField = ({
  name,
  label,
  showLabel,
  value,
  canAddAdjustment,
  canRemoveAdjustment,
  atLeastOneRequired,
  register: shouldRegister,
  inline,
  isEditing,
  readOnly,
}) => {
  useRegisterInput(name, shouldRegister);

  const intl = useIntl();
  const formContext = useFormContext();
  const { watch } = formContext ?? {};
  const inputValue = value ?? (watch && watch(name));
  const { data, append, remove, update } = useArrayField({
    name,
    value: atLeastOneRequired && !inputValue.length ? [ADJUSTMENT_STRUCTURE] : inputValue,
  });

  const handleChange = index => (field, ev, newValue) => {
    update(index, field)(ev, newValue);
  };

  const hasMinimumAdjustments = !atLeastOneRequired || (atLeastOneRequired && data.length > 1);
  const isInline = inline && !isEditing;
  const canAdd = !isInline && !readOnly && canAddAdjustment;
  const canRemove = !isInline && !readOnly && canRemoveAdjustment && hasMinimumAdjustments;

  return (
    <FormControl component="fieldset" variant="outlined" fullWidth>
      {showLabel && <FormLabel component="legend">{label ?? capitalize(name)}</FormLabel>}
      {data.map((adjustment, idx) => (
        <Box py={1} key={adjustment.reference}>
          <Card>
            <CardContent>
              <Adjustment
                adjustment={adjustment}
                onChange={handleChange(idx)}
                register={false}
                inline={inline}
                isEditing={isEditing}
                readOnly={readOnly}
              />
            </CardContent>
            {canRemove && (
              <CardActions>
                <Box marginLeft="auto" px={1} pb={1}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    size="small"
                    onClick={() => remove(idx)}
                  >
                    {intl.formatMessage(translations.remove)}
                  </Button>
                </Box>
              </CardActions>
            )}
          </Card>
        </Box>
      ))}
      {canAdd && (
        <Button
          color="primary"
          variant="outlined"
          size="small"
          onClick={() => append({ ...ADJUSTMENT_STRUCTURE, reference: uuid() })}
        >
          {intl.formatMessage(translations.add)}
        </Button>
      )}
    </FormControl>
  );
};

AdjustmentsField.propTypes = {
  atLeastOneRequired: PropTypes.bool,
  canAddAdjustment: PropTypes.bool,
  canRemoveAdjustment: PropTypes.bool,
  inline: PropTypes.bool,
  isEditing: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  readOnly: PropTypes.bool,
  register: PropTypes.bool,
  showLabel: PropTypes.bool,
  value: PropTypes.array,
};

AdjustmentsField.defaultProps = {
  atLeastOneRequired: false,
  canAddAdjustment: true,
  canRemoveAdjustment: true,
  inline: false,
  isEditing: false,
  label: undefined,
  name: 'adjustments',
  readOnly: false,
  register: true,
  showLabel: true,
  value: undefined,
};

export default AdjustmentsField;
